import { arrayMoveImmutable } from "array-move";
import Konva from "konva";
import React, {
  cloneElement,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { useMainHook } from "../../../hooks/main";
import { useHeaderEditor } from "./headerEditor";
import { useSelection } from "./selection";
import cloneDeep from "lodash/cloneDeep";
import { forEach } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { useToolbarEditor } from "./toolbarEditor";
import { useTextsEdition } from "./textsEdition";

interface PagesEditorProviderProps {
  children: ReactNode;
}

interface PagesEditorData {
  handleRemovePage: (page: number) => void;
  handleOrderPageMoveDown: (page: number) => void;
  handleOrderPageMoveUp: (page: number, index: number) => void;
  handleDuplicatePage: (page: number, index: number) => void;
  isActiveNewPage: {
    visible: boolean;
    index: number;
  };
  setIsActiveNewPage: React.Dispatch<
    React.SetStateAction<{
      visible: boolean;
      index: number;
    }>
  >;
  handleAddNewPage: (page: number) => void;
  handleHoverIcon: (index: number, visible: boolean) => void;
  isActiveDuplicatePage: {
    visible: boolean;
    index: number;
  };
  setIsActiveDuplicatePage: React.Dispatch<
    React.SetStateAction<{
      visible: boolean;
      index: number;
    }>
  >;
  handleHoverIconDuplicatePage: (index: number, visible: boolean) => void;
  isActiveHoverIconRemovePage: {
    visible: boolean;
    index: number;
  };
  setIsActiveHoverIconRemovePage: React.Dispatch<
    React.SetStateAction<{
      visible: boolean;
      index: number;
    }>
  >;
  handleHoverIconRemovePage: (index: number, visible: boolean) => void;
  idPage: number;
  setIdPage: React.Dispatch<React.SetStateAction<number>>;
  isPageOneOnly: boolean;
  setIsPageOneOnly: React.Dispatch<React.SetStateAction<boolean>>;
  isVisibleAddPage: boolean;
  setIsVisibleAddPage: React.Dispatch<React.SetStateAction<boolean>>;
  disabledButton: {
    visible: boolean;
    index: number;
  };

  setDisabledButton: React.Dispatch<
    React.SetStateAction<{
      visible: boolean;
      index: number;
    }>
  >;
  stagePointerPosition: { x: number; y: number };
  setStagePointerPosition: React.Dispatch<
    React.SetStateAction<{
      x: number;
      y: number;
    }>
  >;

  copyObject: () => void;
  pasteObject: () => void;

  scaleForZoom: { x: number; y: number; scale: number };
  setScaleForZoom: React.Dispatch<
    React.SetStateAction<{
      x: number;
      y: number;
      scale: number;
    }>
  >;
}

const PagesEditorContext = createContext({} as PagesEditorData);

const PagesEditorProvider: React.FC = ({
  children
}: PagesEditorProviderProps) => {
  const {
    objectScreen,
    setObjectScreen,
    setObjectSelectId,

    stageRef,
    addToHistory
  } = useMainHook();
  const {
    selectedObject,
    selectedObjects,
    setSelectedObject,
    setSelectedObjects,
    IsModalVisible,
    setIsModalVisible,
    currentMultipleSelection
  } = useSelection();

  const { isEditing, blockingKeyboard } = useTextsEdition();

  const [idPage, setIdPage] = useState(1);

  const { blockCommandKeyboard, forceBlockKey, setForceBlockKey } =
    useToolbarEditor();
  const [stagePointerPosition, setStagePointerPosition] = useState({
    x: 0,
    y: 0
  });
  const [scaleForZoom, setScaleForZoom] = useState({ x: 0, y: 0, scale: 1 });
  const [isVisibleAddPage, setIsVisibleAddPage] = useState(false);
  const [automaticSelect, setAutomaticSelect] = useState<string>(null);
  const [isPageOneOnly, setIsPageOneOnly] = useState(false);
  const [, updateState] = useState<any>();
  const [copyElement, setCopyElement] = useState<Konva.Shape>(null);
  const [pasteMultipleElements, setPasteMultipleElements] = useState([]);
  const [copyElements, setCopyElements] = useState<any>([]);
  const [disabledButton, setDisabledButton] = useState({
    visible: true,
    index: 0
  });

  const [isActiveDuplicatePage, setIsActiveDuplicatePage] = useState({
    visible: false,
    index: 0
  });

  const [isActiveNewPage, setIsActiveNewPage] = useState({
    visible: false,
    index: 0
  });

  const [isActiveHoverIconRemovePage, setIsActiveHoverIconRemovePage] =
    useState({
      visible: false,
      index: 0
    });

  const myFunc = useCallback(
    (event: KeyboardEvent) => {
      let charCode = String.fromCharCode(event.which).toLowerCase();

      if ((event.ctrlKey || event.metaKey) && charCode === "c") {
        copyObject();
      } else if ((event.ctrlKey || event.metaKey) && charCode === "v") {
        pasteObject();
      }
    },
    [
      blockCommandKeyboard,
      forceBlockKey,
      selectedObject,
      selectedObjects,
      isEditing,
      blockingKeyboard
    ]
  );

  useEffect(() => {
    document.addEventListener("keydown", myFunc, true);
    return () => document.removeEventListener("keydown", myFunc, true);
  }, [selectedObject, selectedObjects, blockCommandKeyboard, forceBlockKey]);

  const forceUpdate = React.useCallback(() => updateState({}), [scaleForZoom]);

  const handleAddNewPage = useCallback(
    (page?: number) => {
      addToHistory();

      setObjectScreen(prevState => {
        let numberOfpages = prevState.length;
        let cloneState = cloneDeep(prevState);
        let newCloneState = cloneState.map((el, index) => {
          return {
            pageNumber: index + 1,
            renderObjects: el?.renderObjects
          };
        });
        let arrayFinally = [...newCloneState];
        arrayFinally.push({
          pageNumber: Number(numberOfpages + 1),
          renderObjects: []
        });

        let lastPageNew = arrayFinally.length - 1;
        const fromIndex = page;
        const endArray = arrayMoveImmutable(
          arrayFinally,
          lastPageNew,
          fromIndex
        );

        const reorderPageNumber = endArray.map((el, index) => {
          return {
            pageNumber: index + 1,
            renderObjects: el.renderObjects
          };
        });

        return reorderPageNumber;
      });
    },
    [objectScreen]
  );

  const handleRemovePage = useCallback(
    (page: number) => {
      addToHistory();

      setObjectScreen(state => {
        let cloneState = cloneDeep(state);
        const myArray = [];

        cloneState.forEach((pageObject, index) => {
          if (Number(page) !== index + 1) {
            pageObject.pageNumber = myArray.length + 1;
            myArray.push(pageObject);
          }
        });

        return myArray;
      });
    },
    [objectScreen]
  );

  const handleOrderPageMoveDown = useCallback(
    (page: number) => {
      setObjectScreen(state => {
        let cloneState = cloneDeep(state);
        let pages = stageRef.current;
        const findPage = pages.findIndex(object => object.attrs.id == page);
        let formattedState = [];

        pages.forEach((objectPage, index) => {
          let pageNumberOfPages = index + 1;
          let renderObjectsOfPages = [];

          objectPage.children[0].children.forEach((item, index) => {
            let cloneAttrs = cloneDeep(item.attrs);
            renderObjectsOfPages.push(cloneAttrs);
          });

          formattedState.push({
            pageNumber: pageNumberOfPages,
            renderObjects: renderObjectsOfPages
          });
        });

        cloneState = formattedState;

        let fromIndex = findPage;
        let toIndex = findPage + 1;

        let newState = arrayMoveImmutable(cloneState, fromIndex, toIndex);

        newState.forEach((object, index) => {
          object.pageNumber = index + 1;
          object.renderObjects = object.renderObjects;
        });
        addToHistory();
        return newState;
      });
    },

    [objectScreen]
  );

  const handleOrderPageMoveUp = useCallback(
    (page: number, index: number) => {
      setObjectScreen(state => {
        let cloneState = cloneDeep(state);
        let pages = stageRef.current;
        const findPage = pages.findIndex(object => object.attrs.id == page);
        let formattedState = [];
        pages.forEach((objectPage, index) => {
          let pageNumberOfPages = index + 1;
          let renderObjectsOfPages = [];
          objectPage.children[0].children.forEach((item, index) => {
            let cloneAttrs = cloneDeep(item.attrs);
            renderObjectsOfPages.push(cloneAttrs);
          });
          formattedState.push({
            pageNumber: pageNumberOfPages,
            renderObjects: renderObjectsOfPages
          });
        });
        cloneState = formattedState;
        let fromIndex = findPage;
        let toIndex = findPage - 1;
        let newState = arrayMoveImmutable(cloneState, fromIndex, toIndex);
        newState.forEach((object, index) => {
          object.pageNumber = index + 1;
          object.renderObjects = object.renderObjects;
        });
        addToHistory();
        return newState;
      });
    },
    [objectScreen]
  );

  const handleDuplicatePage = useCallback(
    (page: number, index: number) => {
      setObjectScreen(prevState => {
        let cloneState = cloneDeep(prevState);
        let arrayFinally = cloneDeep(cloneState);
        let pages = stageRef.current;
        let newPages = [];
        const findStage = pages.find(el => el.attrs.id == page);
        const selectedObjs = findStage?.children[0].children;
        selectedObjs.forEach((values, index) => {
          let pageNumber = values.attrs.id;
          // let newObjs = { ...item.attrs, id: String(Math.random() * 2) };
          let cloneValues = cloneDeep(values);

          if (values?.attrs?.object === "richTextImage") {
            let cloneValues = cloneDeep(values.attrs);
            let cloneAttrs = {
              cornerRadius: cloneValues.cornerRadius,
              draggable: cloneValues.draggable,
              format: cloneValues.format,
              height: cloneValues.height,
              id: uuidv4(),
              name: cloneValues.name,
              object: cloneValues.object,
              offsetX: cloneValues.offsetX,
              offsetY: cloneValues.offsetY,
              page: cloneValues.page,
              rotation: cloneValues.rotation,
              scaleX: cloneValues.scaleX,
              scaleY: cloneValues.scaleY,
              skewX: cloneValues.skewX,
              skewY: cloneValues.skewY,
              src: cloneValues.src,
              text: cloneValues.text,
              textHTML: cloneValues.textHTML,
              width: cloneValues.width,
              x: cloneValues.x,
              y: cloneValues.y,
              initialDelta: {}
            };

            newPages.push({ ...cloneAttrs });
          } else {
            let cloneAttrs = {
              ...cloneValues.attrs,
              id: uuidv4()
            };
            newPages.push(cloneAttrs);
          }
        });

        arrayFinally.push({
          pageNumber: page + 1,
          renderObjects: newPages
        });

        let numberOfpages = arrayFinally.length;
        const fromIndex = numberOfpages - 1;
        const toIndex = page;
        const endArray = [
          ...arrayMoveImmutable(arrayFinally, fromIndex, toIndex)
        ];

        const reorderPageNumber = endArray.map((el, index) => {
          return {
            pageNumber: index + 1,
            renderObjects: [...el.renderObjects]
          };
        });

        addToHistory();

        return reorderPageNumber;
      });
    },
    [objectScreen]
  );

  const copyObject = useCallback(() => {
    if (blockCommandKeyboard || forceBlockKey) {
      return;
    }

    if (IsModalVisible) {
      return;
    }

    //addToHistory();
    if (selectedObject) {
      setAutomaticSelect(null);
      setCopyElements(oldState => {
        let newState = [];
        return newState;
      });

      setCopyElement(prevState => {
        let cloneState = cloneDeep(prevState);
        cloneState = selectedObject;
        return cloneState;
      });
      setSelectedObject(null);
    }

    if (!selectedObject) {
      setAutomaticSelect(null);
      setCopyElement(null);
      setCopyElements(oldState => {
        let cloneState = cloneDeep(oldState);
        cloneState = selectedObjects;
        return cloneState;
      });
      setSelectedObject(null);
      currentMultipleSelection.current.setNodes([]);
      setSelectedObjects([]);
    }
  }, [
    selectedObject,
    selectedObjects,
    copyElement,
    copyElements,
    blockCommandKeyboard,
    forceBlockKey,
    isEditing,
    blockingKeyboard,
    automaticSelect
  ]);

  useEffect(() => {
    if (!!automaticSelect) {
      if (copyElement && copyElements?.length === 0) {
        objectScreen?.forEach(page => {
          page?.renderObjects?.forEach((renderedObject: any) => {
            if (renderedObject?.id === automaticSelect) {
              const findStage: Konva.Stage = stageRef.current?.find(
                el => el?.attrs?.id == idPage
              );
              const result = findStage?.find(`#${automaticSelect}`);
              const ShapeResult: any = result[0];
              setSelectedObject(ShapeResult);
            }
          });
        });
      }
      setCopyElement(null);
    }
  }, [automaticSelect]);

  useEffect(() => {
    if (pasteMultipleElements) {
      const nodes = [];
      const findStage: Konva.Stage = stageRef.current?.find(
        el => el.attrs.id === idPage
      );
      pasteMultipleElements?.forEach(object => {
        const result = findStage?.find(`#${object.id}`);
        const shapeResult = result[0];
        nodes.push(shapeResult);
      });

      currentMultipleSelection?.current?.setNodes(nodes);
      setSelectedObjects(nodes);

      let colectObjectsName = currentMultipleSelection.current
        ?.nodes()
        ?.map(object => {
          return object?.attrs?.object;
        });

      if (
        colectObjectsName?.includes("richTextImage") ||
        colectObjectsName?.includes("simpleLine")
      ) {
        currentMultipleSelection?.current?.anchorSize(0);
        currentMultipleSelection?.current?.rotateAnchorOffset(0);
      } else {
        currentMultipleSelection?.current?.anchorSize(10);
        currentMultipleSelection?.current?.rotateAnchorOffset(50);
      }
    }
  }, [pasteMultipleElements]);

  const pasteObject = useCallback(() => {
    if (blockCommandKeyboard || forceBlockKey) {
      return;
    }

    if (IsModalVisible) {
      return;
    }

    if (copyElement || copyElements) {
      addToHistory();
      setObjectScreen(prevState => {
        let cloneState = cloneDeep(prevState);

        let newObjects = [];

        if (copyElements?.length === 0) {
          let cloneAttrs = cloneDeep(copyElement?.attrs);

          if (copyElement?.attrs?.object === "richTextImage") {
            const myIdObject = uuidv4();
            setAutomaticSelect(myIdObject);
            const widthCanvas: number = 596;
            const heigthCanvas: number = 842;
            let newObjectCloned: any = {
              cornerRadius: cloneAttrs?.cornerRadius,
              draggable: cloneAttrs?.draggable,
              format: cloneAttrs?.format,
              height: cloneAttrs?.height,
              id: myIdObject,
              name: cloneAttrs?.name,
              object: cloneAttrs?.object,
              offsetX: cloneAttrs?.offsetX,
              offsetY: cloneAttrs?.offsetY,
              page: cloneAttrs?.page,
              rotation: cloneAttrs?.rotation,
              scaleX: cloneAttrs?.scaleX,
              scaleY: cloneAttrs?.scaleY,
              skewX: cloneAttrs?.skewX,
              skewY: cloneAttrs?.skewY,
              src: cloneAttrs?.src,
              text: cloneAttrs?.text,
              textHTML: cloneAttrs?.textHTML,
              width: cloneAttrs?.width,
              x: Math.ceil(widthCanvas / 2 - cloneAttrs?.width / 2),
              y: Math.ceil(heigthCanvas / 2 - cloneAttrs?.height / 2),
              initialDelta: {}
            };

            cloneState[idPage - 1]?.renderObjects?.push({
              ...newObjectCloned
            });
          } else {
            const widthCanvas: number = 596;
            const heigthCanvas: number = 842;
            const myIdObject = uuidv4();

            let myObjectPasted = {
              ...copyElement?.attrs,
              id: myIdObject,
              x:
                copyElement?.attrs?.object === "rectangle"
                  ? Math.ceil(widthCanvas / 2 - cloneAttrs?.width / 2)
                  : Math.ceil(widthCanvas / 2),
              y:
                copyElement?.attrs?.object === "rectangle"
                  ? Math.ceil(heigthCanvas / 2 - cloneAttrs?.height / 2)
                  : Math.ceil(heigthCanvas / 2)
            };

            cloneState[idPage - 1].renderObjects.push({
              ...myObjectPasted
            });

            setAutomaticSelect(myIdObject);
          }
        } else {
          setAutomaticSelect(null);
          copyElements?.forEach((object, index) => {
            if (object?.attrs?.object === "richTextImage") {
              let cloneAttrs = cloneDeep(object.attrs);
              let myUUID = uuidv4();
              let newObjectCloned = {
                id: myUUID,
                cornerRadius: cloneAttrs?.cornerRadius,
                draggable: cloneAttrs?.draggable,
                format: cloneAttrs?.format,
                height: cloneAttrs?.height,
                name: cloneAttrs?.name,
                object: cloneAttrs?.object,
                offsetX: cloneAttrs?.offsetX,
                offsetY: cloneAttrs?.offsetY,
                page: cloneAttrs?.page,
                rotation: cloneAttrs?.rotation,
                scaleX: cloneAttrs?.scaleX,
                scaleY: cloneAttrs?.scaleY,
                skewX: cloneAttrs?.skewX,
                skewY: cloneAttrs?.skewY,
                src: cloneAttrs?.src,
                text: cloneAttrs?.text,
                textHTML: cloneAttrs?.textHTML,
                width: cloneAttrs?.width,
                x: cloneAttrs?.x + 50,
                y: cloneAttrs?.y + 50,
                initialDelta: ""
              };

              newObjects?.push(newObjectCloned);
              setPasteMultipleElements([...newObjects]);
            } else {
              newObjects?.push({
                ...object?.attrs,
                id: uuidv4(),
                x: object?.attrs.x + 50,
                y: object?.attrs.y + 50
              });

              setPasteMultipleElements([...newObjects]);
            }
          });

          cloneState[idPage - 1]?.renderObjects?.push(...newObjects);
        }

        return cloneState;
      });
    }
  }, [
    selectedObject,
    selectedObjects,
    copyElement,
    copyElements,
    blockCommandKeyboard,
    forceBlockKey,
    isEditing,
    blockingKeyboard,
    objectScreen,
    stageRef,
    idPage,
    pasteMultipleElements
  ]);

  const handleHoverIcon = (index: number, visible: boolean) => {
    setIsActiveNewPage({
      visible,
      index
    });
  };

  const handleHoverIconDuplicatePage = (index: number, visible: boolean) => {
    setIsActiveDuplicatePage({
      visible,
      index
    });
  };

  const handleHoverIconRemovePage = (index: number, visible: boolean) => {
    addToHistory();
    setIsActiveHoverIconRemovePage({
      visible,
      index
    });
  };

  useEffect(() => {
    const numberOfPages = objectScreen.length;
    if (numberOfPages > 1) {
      setIsVisibleAddPage(false);
      setIsPageOneOnly(false);
    } else {
      setIsVisibleAddPage(true);
      setIsPageOneOnly(true);
    }
  }, [objectScreen]);

  useEffect(() => {
    if (scaleForZoom) {
      forceUpdate();
      setObjectScreen(prevState => {
        const clone = cloneDeep(prevState);
        return clone;
      });
    }
  }, [scaleForZoom]);

  return (
    <PagesEditorContext.Provider
      value={{
        handleRemovePage,
        handleOrderPageMoveDown,
        handleOrderPageMoveUp,
        handleDuplicatePage,
        setIsActiveNewPage,
        isActiveNewPage,
        handleAddNewPage,
        handleHoverIcon,
        handleHoverIconDuplicatePage,
        isActiveDuplicatePage,
        setIsActiveDuplicatePage,
        isActiveHoverIconRemovePage,
        setIsActiveHoverIconRemovePage,
        handleHoverIconRemovePage,
        idPage,
        setIdPage,
        isVisibleAddPage,
        setIsVisibleAddPage,
        isPageOneOnly,
        setIsPageOneOnly,
        disabledButton,
        setDisabledButton,
        copyObject,
        pasteObject,
        stagePointerPosition,
        setStagePointerPosition,
        scaleForZoom,
        setScaleForZoom
      }}
    >
      {children}
    </PagesEditorContext.Provider>
  );
};

// creating hook

function usePagesEditor(): PagesEditorData {
  const context = useContext(PagesEditorContext);

  if (!context) {
    throw new Error(
      "useToolbarEditor must be used with an ToolbarEditorProvider.. "
    );
  }

  return context;
}

export { PagesEditorProvider, usePagesEditor };
